import { useIntl } from 'react-intl';
import React, { useCallback, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { GenericMessages } from '../../../intl/GenericMessages';
import { DqText } from '../../dq-text';
import './BundleUploadModal.scss';
import { BundleMessages } from '../../../intl/BundleMessages';
import FileUploadForm from '../../reusable-components/file-upload-form/FileUploadForm';
import { CreateBundleRequest } from '../../../redux/api/bundles';
import { AppModal } from '../../reusable-components/app-modal/AppModal';
import { parseBundleDetails, trimBundleGrid } from '../../../utils/helpers/bundleParserHelpers';
import { BundleTranslator } from '../../../utils/helpers/BundleTranslator';
import { TubeStatus_t } from 'foreman-slink';

interface BundleUploadModalProps {
  handleSave: (bundle?: CreateBundleRequest) => void;
  isOpen: boolean;
}

/**
 * Parsing result type from papaparse documentation - https://react-papaparse.js.org/docs#results
 */
interface CSVResult {
  data: string[][];
  errors: {
    type: string;
    code: string;
    message: string;
    row: number;
  }[];
  // Documentation is unclear on types and consistency of meta properties.
  meta: any;
}

export const BundleUploadModal: React.FC<BundleUploadModalProps> = props => {
  const { formatMessage } = useIntl();
  const { readString } = usePapaParse();
  const [bundleError, setBundleError] = useState<string | undefined>();
  const [bundleWarning, setBundleWarning] = useState<string | undefined>();

  const [request, setRequest] = useState<CreateBundleRequest | undefined>();

  const onSubmission = useCallback(async () => {
    setBundleError(undefined);

    if (!request) {
      setBundleError(formatMessage(BundleMessages.bundleFileRequiredError));
      return;
    }

    setRequest(undefined);
    props.handleSave(request);
  }, [formatMessage, props, request]);

  const onFileLoad = async (file: File) => {
    if (!file) {
      console.warn('File load unsuccessful, file is null');
    }
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      if (!target?.result) {
        console.warn('Load target result was null');
        setBundleError(formatMessage(BundleMessages.uploadBundleError));
        return;
      }

      if (typeof target.result !== 'string') {
        console.warn('File read did not result in a string.');
        setBundleError(formatMessage(BundleMessages.uploadBundleError));
        return;
      }

      readString(target.result as string, {
        worker: true,
        complete: (results: CSVResult) => {
          if (!results || !results.data?.length || !!results.errors?.length) {
            console.warn('File parsing created empty results or a result with errors.');
            const firstError = results.errors.find(e => e.row === Math.min(...results.errors.map(error => error.row)));
            const messageWithRow = formatMessage(BundleMessages.bundleErrorMessageWithRow, {
              row: firstError?.row,
              message: firstError?.message,
            });
            setBundleError(formatMessage(BundleMessages.bundleUploadedInvalidWithError, { error: messageWithRow }));
            return;
          }
          const { success, request, error } = parseBundleDetails(results.data, formatMessage);

          if (!success) {
            setBundleError(formatMessage(BundleMessages.bundleUploadedInvalidWithError, { error }));
            return;
          }

          const bundleGrid = results.data.splice(15);

          const trimmedBundleGrid = trimBundleGrid(bundleGrid);

          // If the total rows exceeds the maximum supported size, throw.
          if (trimmedBundleGrid.length > 300) {
            setBundleError(formatMessage(BundleMessages.bundleToLargeError));
            return;
          }

          // We want to count up on the Y axis, so this allows us to iterate from the bottom of the trimmed grid.
          trimmedBundleGrid.reverse();

          // Used to stop processing if maximum supports blank row count is met
          // which means we are at the end of the mapping section and trimming white space.
          const maxBlankRowCount = 50;
          let continuousBlankRowCount = 0;
          // Each row represents a cartesian "Y" coordinate
          for (let y = 0; y < trimmedBundleGrid.length; y++) {
            // Check for end of file
            if (!trimmedBundleGrid[y] || continuousBlankRowCount === maxBlankRowCount) {
              break;
            }

            // If blank row, increment continuous blank row count and move to next row
            if (trimmedBundleGrid[y].every((cell: string) => !cell || !cell.trim())) {
              continuousBlankRowCount++;
              continue;
            }

            // We have a row with data, reset continuous blank row count
            continuousBlankRowCount = 0;

            // If any row exceeds the maximum supported column length, throw.
            if (trimmedBundleGrid[y].length > 300) {
              setBundleError(formatMessage(BundleMessages.bundleToLargeError));
              return;
            }

            // Iterate through cells, each cell represents a cartesian "X" coordinate
            for (let x = 0; x < trimmedBundleGrid[y].length; x++) {
              // If blank cell, no tube
              if (!trimmedBundleGrid[y][x] || !trimmedBundleGrid[y][x].trim()) {
                continue;
              }

              // If cell has value, add tube mapping to collection
              request.tubes.push({
                tubeName: trimmedBundleGrid[y][x],
                status: TubeStatus_t.Uncleaned, // TODO: Everything is uncleaned right now, in the future there will be different statuses
                xCoordinate: x,
                yCoordinate: y,
              });
            }
          }

          // Validate request
          const mappedTubes = BundleTranslator.transformTubesFromPattern(
            request.tubes,
            request.pattern,
            request?.rotated ?? false,
          );
          const validTubes = mappedTubes.filter(tube => Number.isInteger(tube.row) && Number.isInteger(tube.column));
          const invalidTubes = mappedTubes.filter(
            tube => !Number.isInteger(tube.row) || !Number.isInteger(tube.column),
          );
          const invalidTubeRow = invalidTubes[0];
          console.log('invalid tubes', invalidTubes, invalidTubeRow);

          // If row or column coordinates contain decimals, the bundle was not mapped correctly and those tubes will be removed.
          if (mappedTubes.length !== validTubes.length) {
            setBundleWarning(formatMessage(BundleMessages.bundleContainsInvalidTubesWarning));
          }

          // Future TODO: Save Sentinel Coordinates since we need to calculate to validate the save?
          request.tubes = validTubes.map(st => {
            return {
              tubeName: st.tubeName,
              status: st.status,
              xCoordinate: st.xCoordinate,
              yCoordinate: st.yCoordinate,
            };
          });

          // If value was not provided for tube count, calculate from length of mapped tubes.
          if (!request.tubeCount?.trim()) {
            request.tubeCount = request.tubes.length.toString();
          }

          setRequest(request);
        },
      });
    };

    reader.readAsText(file);
  };

  const onClose = useCallback(() => {
    setRequest(undefined);
    setBundleError(undefined);
    setBundleWarning(undefined);
    props.handleSave();
  }, [props]);

  const onFileClear = () => {
    setRequest(undefined);
    setBundleError(undefined);
    setBundleWarning(undefined);
  };

  const submitButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.submitButtonLabel),
      onClick: onSubmission,
    };
  }, [formatMessage, onSubmission]);

  const cancelButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.cancelButtonLabel),
      onClick: onClose,
    };
  }, [formatMessage, onClose]);

  return (
    <AppModal
      isOpen={props.isOpen}
      title={formatMessage(BundleMessages.uploadBundleTitle)}
      onClose={onClose}
      primaryButton={submitButton}
      secondaryButton={cancelButton}
    >
      <div>
        <FileUploadForm
          onSave={onFileLoad}
          acceptableFileTypes=".txt, text/plain, .csv, text/csv"
          useFileState={true}
          onClear={onFileClear}
          headingText={formatMessage(BundleMessages.bundleFileUploadDescription)}
          hasError={!!bundleError}
        />
        {bundleError && (
          <div>
            <DqText className="bundle-upload-error-asterisk" variant="body-strong">
              *
            </DqText>

            <DqText className="bundle-upload-error" variant="body-strong">
              {bundleError}
            </DqText>
          </div>
        )}
        {bundleWarning && (
          <DqText className="bundle-upload-warning" variant="body-strong">
            {bundleWarning}
          </DqText>
        )}
      </div>
    </AppModal>
  );
};
